import { Injectable } from "@angular/core";
import { FormGroup } from "@angular/forms";
import { ACTIONS, DialogResult } from "src/app/global/models/dialog/dialog-data";
import { DialogService } from "src/app/view-model/services/dialog.service";
import { MarketplaceRedirectService } from "src/app/view-model/services/marketplace-redirect-vm.service";
import _ from 'lodash';
import { GeneralResponse } from "src/app/global/interfaces/general-response";
import { RutFormatterService } from "src/app/view-model/services/rut-formatter.service";
import { DatosSociosService } from "src/app/global/services/datos-socios.service";
import { LocalStorageService } from "src/app/global/services/localstorage.service";
import { SessionService } from "src/app/global/services/session.service";

@Injectable({ providedIn: 'root' })
export class ValidacionCIPreFirmaVM {
    constructor(
        private rutFormatter: RutFormatterService,
        private dialogService: DialogService,
        private datosSociosService: DatosSociosService,
        private marketplaceRedirectService: MarketplaceRedirectService,
        private localStorageService: LocalStorageService,
         private sessionService: SessionService
        
    ) {}


    /**
     * Verifica si la cédula de identidad está vigente en base al contenido recibido.
     * 
     * Esta función analiza el contenido para identificar un posible estado de validación de CI.
     * Si no se detecta ningún estado relevante, se considera que puede continuar el flujo.
     * 
     * - Si el estado detectado requiere una validación adicional (por ejemplo, "NO_VIGENTE"),
     *   se recupera el RUN desde localStorage y se abre un diálogo de validación de CI.
     *   Si el resultado es "VIGENTE", se resuelve como validado; de lo contrario, se rechaza.
     * 
     * - Si el estado requiere una acción por parte de la UI (como "INTENTOS_AGOTADOS" o "PENDIENTE"),
     *   se delega la acción a `gestionarRespuestaEstadoCI` y se maneja como error si corresponde.
     * 
     * @param contenido - Cadena con el contenido que puede contener un estado de validación de CI.
     * @returns Una promesa que resuelve con:
     *   - "CONTINUE": si no se requiere validación adicional.
     *   - "VALIDADO": si se realiza la validación con éxito.
     *   - Rechaza con "NO_VALIDADO" o "ERROR" en caso de fallo.
     */
    public async assertCedulaIdentidadVigente(contenido:string,runInput?:string|number){

       return new Promise<string>(async (resolve, reject) => {

            if(this.debeValidarCedulaIdentidadVigente(contenido)){

                const stepThree = this.localStorageService.getPaso3();
                const run = runInput?runInput:stepThree.persona.run;

                const respValidacionCI= await this.openCiValidationDialog(String(run));
                if(respValidacionCI ==='VIGENTE'){
                    resolve( "VALIDADO");
                    return;
                }
                reject("NO_VALIDADO");
                return;

            }

            resolve( "CONTINUE");
            return;         
        });
    }

    public  debeValidarCedulaIdentidadVigente(contenido:string):boolean{

            const estadosPorValidarCI = ['NO_VIGENTE']
            const estadoValidacionCI = this.findEstadoValidacionCI(contenido);        
            if("" === estadoValidacionCI){                 
                 return false;
             }

             if(estadosPorValidarCI.includes(estadoValidacionCI)){
                return true;
             }

             this.gestionarRespuestaErrorEstadoCI(estadoValidacionCI);        
             throw "ERROR AL OBTENER ESTADO DE VALIDACIÓN DE CI";    
     }
             
    /**
     * Extrae el estado de validación de CI desde un mensaje de error o contenido.
     */
    private findEstadoValidacionCI(contenido: string): string {
        const regex = /EstadoIncorrectoCedulaIdentidad\[(.*?)\]/;
        const match = contenido.match(regex);
        if (match) return match[1];

        if (contenido.includes('se le han agotado los intentos') || contenido.includes('INTENTOS_AGOTADOS')) {
            return "INTENTOS_AGOTADOS";
        }
            
        if (_.includes(contenido, 'NO_VIGENTE')) {
            return "NO_VIGENTE";
        }
        if ( ['NO_VIGENTE','VIGENTE','PENDIENTE'].some(estado=>estado === contenido)) {
            return contenido;
        }

        return "";
    }

    /**
     * Abre el diálogo de validación de CI y maneja las posibles respuestas.
     */
    public openCiValidationDialog(run: string): Promise<string> {
        const dv = this.rutFormatter.getDv(Number(run));
        const rut = this.rutFormatter.format(`${run}${dv}`);

        return new Promise((resolve, reject) => {
            const dialogRef = this.dialogService.openCiValidationDialog(rut, { preventDefaultSubmit: true });
            dialogRef.afterClosed().subscribe(async (result: DialogResult) => {
                
                if (!result || !result.action || !result.data || result.action !== ACTIONS.OK){reject("NO_VALIDATED"); return}

                try {
                    const resultado = await this.onSubmitValidate(result.data);

                    switch (resultado.estado) {
                        case "INTENTOS_AGOTADOS":
                            this.dialogService.openAccountBloquedDialog().afterClosed().subscribe(() => {
                                this.redirectToMarketplace();
                            });
                            break;
                        case "NO_VIGENTE":
                            const dialog = String(resultado.data) === '2'
                                ? this.dialogService.openInvalidSerieOfRutFirstAttemptDialog()
                                : this.dialogService.openInvalidSerieOfRutSecondAttemptDialog();
                            dialog.afterClosed().subscribe(() => {
                                this.openCiValidationDialog(run).then(result => {
                                    resolve(result)
                                 }
                             )});
                            break;
                        case "VIGENTE":
                            resolve("VIGENTE");
                            break;
                        case "PENDIENTE":
                        const ejecutivo = this.sessionService.getEjecutivo();  

                        const modalRef = ejecutivo
                            ? this.dialogService.openPendienteContactoEjecutivoBO()
                            : this.dialogService.openPendienteContactoEjecutivo();

                        modalRef.afterClosed().subscribe(() => {
                            this.redirectToMarketplace();
                            resolve("Error");
                        });
                        break;  
                        default:
                            this.dialogService.openErrorDialog('Lo sentimos', 'Estamos presentando problemas, no podemos cargar la página.<br>Por favor, intenta más tarde.')
                                .afterClosed().subscribe(() => {
                                    this.redirectToMarketplace();
                                    reject("Error");
                                });
                            resolve("Error");
                            break;
                    }
                } catch (err) {
                    this.dialogService.openErrorDialog('Error', 'Ha ocurrido un error inesperado.')
                        .afterClosed().subscribe(() => this.redirectToMarketplace());
                    reject(err);
                }
            });
        });
    }

    /**
     * Valida los datos del formulario de CI y retorna el estado evaluado.
     */
    private async onSubmitValidate(datosCiForm: FormGroup): Promise<{ estado: string; data?: any }> {
        try {
            const response = await this.validateCi(datosCiForm);
            const estado = this.findEstadoValidacionCI(response.data) || this.findEstadoValidacionCI(response.indicador) || this.findEstadoValidacionCI(_.get(response, 'mensaje.contenido'));
            return { estado, data: response.data };
        } catch (exception) {
            const estado = this.findEstadoValidacionCI(_.get(exception, 'error.mensaje.contenido')) || this.findEstadoValidacionCI(_.get(exception, 'error.indicador'));
            return { estado, data: _.get(exception, 'error.data', '') };
        }
    }

    /**
     * Llama al servicio para validar la CI con los datos formateados del formulario.
     */
    private async validateCi(form: FormGroup): Promise<GeneralResponse<any>> {
        if (!form.valid) throw new Error('Formulario inválido');

        const serie = form.get('txtSerie').value.replace(/\./g, '');
        const rut = form.get('txtRut').value;
        const rutFormatted = this.rutFormatter.getRutFormats(rut);

        return await this.datosSociosService.validaCiPendiente(rutFormatted.numbers, serie).toPromise();
    }

    /**
     * Redirecciona al usuario al marketplace.
     */
    private redirectToMarketplace(): void {
        this.marketplaceRedirectService.redirect();
    }

    /**
     * Valida el contenido recibido y ejecuta una acción dependiendo del estado.
     */
    private gestionarRespuestaErrorEstadoCI(estado: string): string {
        switch (estado) {
            case "INTENTOS_AGOTADOS":
                this.dialogService.openAccountBloquedDialog().afterClosed().subscribe(() => this.redirectToMarketplace());
                return "ERROR";
            case "PENDIENTE":
                const ejecutivo = this.sessionService.getEjecutivo();

                const modalRef = ejecutivo
                    ? this.dialogService.openPendienteContactoEjecutivoBO()
                    : this.dialogService.openPendienteContactoEjecutivo();

                modalRef.afterClosed().subscribe(() => {
                    this.redirectToMarketplace();
                });

                return "ERROR";
            default:
                return estado;
        }
    }
}
